import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Icon, Link, Panel } from "capitalrx-components-2.0/lib/general";
import { Body, Italic, Semibold } from "capitalrx-components-2.0/lib/typography";

import { DrugDetailsResponse, DrugPrice, PharmacyInfo } from "@/api/types";
import { StackOrShelf } from "@/common/components/StackOrShelf";
import { ThemedButton } from "@/premium/themedComponents";
import { useNavigateSearch } from "@/premium/hooks";

import "./PharmacyCard.scss";

const formatTextPascal = (words: string = "") =>
  words
    .split(" ")
    .filter((word) => word) // Filter out any empty strings.
    .map((word) => word[0].toUpperCase() + word.substring(1).toLowerCase())
    .join(" ");

export interface CombinedPharmacyAndDrugInfo {
  readonly pharmacy: PharmacyInfo;
  readonly drug: DrugPrice & DrugDetailsResponse;
};
export interface PharmacyAndDrugProps {
  readonly pharmacyData: CombinedPharmacyAndDrugInfo;
  readonly isNoDeductible?: boolean;
  readonly showGetPharmacyDirectionsButton?: boolean;
}

export const PharmacyCard = ({ pharmacyData, isNoDeductible, showGetPharmacyDirectionsButton }: PharmacyAndDrugProps) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateSearch();
  const [searchParams] = useSearchParams();
  const enrollmentPlanId = searchParams.get("enrollmentPlan");
  const clientId = searchParams.get("client");
  const pharmacy = pharmacyData?.pharmacy;
  const drug = pharmacyData?.drug;

  const { isMobile } = useResponsive(
    useMemo(() => {
      return {
        isMobile: { desktop: false, tablet: false, mobile: true },
      };
    }, []),
  );

  const priceDisplay = useMemo(
    () => {
      if (!drug) {
        return;
      }
      let patientPayAmount = drug?.patient_pay_amount;
      if (isNoDeductible) {
        patientPayAmount = drug?.patient_pay_amount_no_deductible;
        if (patientPayAmount === undefined || patientPayAmount === null) {
          patientPayAmount = drug?.patient_pay_amount;
        }
      }
      if (patientPayAmount === undefined || patientPayAmount === null) {
        return null;
      } else {
        return "$" + patientPayAmount.toFixed(2).toString();
      }
    },
    [isNoDeductible],
  );

  const handleDrugPriceButtonClick = () => {
    navigate("/plans/plan-details/pharmacies/pharmacy-details",
      {
        enrollmentPlan: enrollmentPlanId,
        client: clientId,
        pharmacyName: pharmacy?.name,
        pharmacyNameSearch: searchParams.get("pharmacyNameSearch") || searchParams.get("pharmacyName") || "",
        providerId: pharmacy?.provider_id,
        dosage: drug?.dosage,
        dosageForm: drug?.dosage_form,
        daysSupply: drug?.days_supply,
        quantity: drug?.quantity,
        drugName: drug?.drug_name,
        drugType: drug?.drug_type,
        patientPay: encodeURIComponent(drug?.patient_pay_amount || "0"),
        planPay: encodeURIComponent(drug?.plan_pay_amount || "0"),
        age: searchParams.get("age") || "",
        gender: searchParams.get("gender") || "",
        drugId: searchParams.get("drugId") || "",
        claimRunDate: searchParams.get("claimRunDate") || "",
        zipCode: searchParams.get("zipCode") || "",
      });
  };

  return (
    <Panel className="pharmacy-card-panel">
      <StackOrShelf>
        <Stack spacing="mini">
          <Shelf>
            {pharmacy?.pharmacy_display_rules?.display_tag && <div className="pharmacy-card-tag">{pharmacy.pharmacy_display_rules.display_tag}</div>}
            <Semibold className="pharmacy-card-name">{pharmacy?.name}</Semibold>
            <Shelf.Spacer />
          </Shelf>
          {pharmacy?.pharmacy_display_rules?.description && (
            <Body size="small"><Italic>{pharmacy?.pharmacy_display_rules?.description}</Italic></Body>
          )}
          <Stack spacing="none">
            <Body size="small">{(Math.round(Number(pharmacy?.distance) * 100) / 100).toFixed(2)} {translate("PHARMACIES.pharmacyCard.miles")}</Body>
            <Body size="small">{formatTextPascal(pharmacy?.address_line_1)}</Body>
            <Shelf.Spacer />
          </Stack>
        </Stack>
        <Shelf.Spacer />
        {isMobile && drug && <Body size="small">{translate("FIND_PHARMACIES.findPharmacies.estimatedAmountYouWillPay")}:</Body>}
        {showGetPharmacyDirectionsButton && pharmacy?.name && pharmacy?.latitude && pharmacy?.longitude && (
          <Link to={`https://maps.apple.com/?q=${encodeURIComponent(pharmacy?.name)}&ll=${pharmacy?.latitude},${pharmacy?.longitude}`}>
            <Shelf spacing="mini" alignment="center">
              {translate("PHARMACIES.pharmacyCard.getDirections")}
              <Icon name="link-out" size="small" color="turquoise" />
            </Shelf>
          </Link>
        )}
        {
          drug && priceDisplay && (
            <ThemedButton appearance="secondary" icon="chevron-right" iconPosition="right" onClick={handleDrugPriceButtonClick}>
              {priceDisplay}
            </ThemedButton>
          )
        }
        {
          drug && !priceDisplay && (
            <Body>{translate("FIND_PHARMACIES.findPharmacies.pricingUnavailable")}</Body>
          )
        }
      </StackOrShelf>
    </Panel>
  );
};
